/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { Theme, webLightTheme } from '@fluentui/react-components';
import * as React from 'react';
import { createThemeFromFluentData } from './useOfficeTheme.utils';

declare let Office: any;
declare let OfficeCore: any;

/**
 * Reads current Office Context theme
 */
const getCurrentOfficeTheme = () => {
  const officeTheme = (Office.context as any)?.officeTheme;

  const fluentThemeData = officeTheme?.fluentThemeData;
  const color = fluentThemeData?.Palettes?.Base?.Bkg;
  const isDark = officeTheme?.isDarkTheme ?? false;

  return {
    currentTheme: createThemeFromFluentData(fluentThemeData, isDark),
    color
  };
};

export const useOfficeTheme = (): { theme: Theme; backgroundColor: string | undefined; } => {
  const [theme, setTheme] = React.useState<Theme>(webLightTheme);
  const [backgroundColor, setBackgroundColor] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {

    const { currentTheme: initialTheme, color: initialColor } = getCurrentOfficeTheme();

    setTheme(initialTheme);
    setBackgroundColor(initialColor);

    if (initialColor) {
      document.body.style.backgroundColor = initialColor;
    }

    const handleThemeChangeEvent = () => {
      const { currentTheme: updatedTheme, color: updatedColor } = getCurrentOfficeTheme();

      setTheme(updatedTheme);
      setBackgroundColor(updatedColor);

      if (updatedColor) {
        document.body.style.backgroundColor = updatedColor;
      }

    };

    // This strategy was taken and adapted from the OfficeCopilot excelStartcore package
    if (typeof OfficeCore !== 'undefined' && OfficeCore?.ThemeService) {
      try {
        const ctx = new OfficeCore.RequestContext();
        const service = OfficeCore.ThemeService.newObject(ctx);

        const themeChangeHandler = (newTheme: any) => {
          if (newTheme) {
            handleThemeChangeEvent();
          }
        };

        service.onOfficeThemeChanged.add(themeChangeHandler);

        ctx.sync(); // Successful callback results in registering the event
        
        return () => {
          service.onOfficeThemeChanged.remove(themeChangeHandler);
        };
      } catch (error) {
        console.debug('OfficeCore.ThemeService setup error:', error);
      }
    }
  }, []);

  return { theme, backgroundColor };
};
